import React, { useState, useEffect } from 'react';
import { Users, Gift, Share2, Copy, Trophy, Crown, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../AuthContext';
import api from '../services/api';

const Referrals = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');

  // Fetch referral stats from API
  useEffect(() => {
    const fetchReferralStats = async () => {
      if (!token) return;
      
      try {
        const response = await api.get('/referrals/stats', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferralStats(response.data);
      } catch (error) {
        console.error('Error fetching referral stats:', error);
        toast({
          title: "Error",
          description: "Failed to load referral data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferralStats();
  }, [token, toast]);

  useEffect(() => {
    // Generate referral code based on authenticated user ID
    if (user && user.id) {
      const code = `LOVE${user.id.substring(0, 6).toUpperCase()}`;
      setReferralCode(code);
    }
  }, [user]);

  const tiers = [
    {
      id: 1,
      title: 'Love Starter',
      referrals: 1,
      reward: 'Exclusive Digital Affirmation Pack',
      icon: Gift,
      color: 'rose',
      description: 'Unlock beautiful affirmation cards delivered instantly'
    },
    {
      id: 2,
      title: 'Love Spreader',
      referrals: 3,
      reward: '20% Merch Discount Code',
      icon: Star,
      color: 'orange',
      description: 'Save big on our entire merch collection'
    },
    {
      id: 3,
      title: 'Love Ambassador',
      referrals: 5,
      reward: 'Free Constant Love T-shirt',
      icon: Trophy,
      color: 'purple',
      description: 'Choose from our premium tee collection (shipping covered)'
    },
    {
      id: 4,
      title: 'Love Champion',
      referrals: 10,
      reward: 'Exclusive Hoodie OR Lifetime App Access',
      icon: Crown,
      color: 'amber',
      description: 'The ultimate reward - premium hoodie or lifetime premium features'
    }
  ];

  // Provide default values when referralStats is null (loading) or use actual data
  const stats = referralStats || {
    totalReferrals: 0,
    activeReferrals: 0,
    totalEarnings: 0,
    currentTier: 1
  };

  const currentTier = tiers.find(tier => stats.totalReferrals >= tier.referrals) || tiers[0];
  const nextTier = tiers.find(tier => stats.totalReferrals < tier.referrals);

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link Copied!",
      description: "Share this link with friends to start earning rewards",
      duration: 3000,
    });
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast({
      title: "Code Copied!",
      description: "Your referral code has been copied to clipboard",
      duration: 3000,
    });
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      color: 'green',
      action: () => {
        const message = `💕 I found this amazing self-love app called Constant Love! Get personalized affirmations daily and join me on this journey. Use my code ${referralCode} for 10% off your first merch purchase! ${window.location.origin}/?ref=${referralCode}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
      }
    },
    {
      name: 'Twitter',
      color: 'blue',
      action: () => {
        const message = `🌟 Loving this self-care app @ConstantLoveApp! Daily AI affirmations that actually help. Join me with code ${referralCode} for 10% off! #SelfLove #Mindfulness ${window.location.origin}/?ref=${referralCode}`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`);
      }
    },
    {
      name: 'Facebook',
      color: 'blue',
      action: () => {
        const url = `${window.location.origin}/?ref=${referralCode}`;
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading referral data...</p>
            </div>
          </div>
        ) : (
          <>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-rose-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">Spread the Love</h1>
          </div>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Share Constant Love with friends and unlock amazing rewards. The more love you spread, the more you earn!
          </p>
        </div>

        {/* Current Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <Card className="p-6 text-center bg-white/70 backdrop-blur-sm border-rose-200">
            <div className="text-3xl font-bold text-rose-600 mb-2">{stats.totalReferrals}</div>
            <div className="text-gray-600 text-sm">Total Referrals</div>
          </Card>
          <Card className="p-6 text-center bg-white/70 backdrop-blur-sm border-rose-200">
            <div className="text-3xl font-bold text-green-600 mb-2">{stats.activeReferrals}</div>
            <div className="text-gray-600 text-sm">Active Friends</div>
          </Card>
          <Card className="p-6 text-center bg-white/70 backdrop-blur-sm border-rose-200">
            <div className="text-3xl font-bold text-purple-600 mb-2">{currentTier.title}</div>
            <div className="text-gray-600 text-sm">Current Tier</div>
          </Card>
          <Card className="p-6 text-center bg-white/70 backdrop-blur-sm border-rose-200">
            <div className="text-3xl font-bold text-amber-600 mb-2">
              {nextTier ? nextTier.referrals - stats.totalReferrals : 0}
            </div>
            <div className="text-gray-600 text-sm">
              {nextTier ? `To ${nextTier.title}` : 'Max Tier Reached!'}
            </div>
          </Card>
        </div>

        {/* Referral Link Section */}
        <Card className="p-8 mb-12 bg-white/70 backdrop-blur-sm border-rose-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Your Referral Code</h2>
          
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex-1 bg-rose-50 border border-rose-200 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Your Code:</div>
                <div className="text-2xl font-bold text-rose-600 font-mono">{referralCode}</div>
              </div>
              <Button onClick={copyReferralCode} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button onClick={copyReferralLink} className="flex-1 bg-rose-500 hover:bg-rose-600 text-white">
                <Share2 className="w-4 h-4 mr-2" />
                Copy Referral Link
              </Button>
              
              {shareOptions.map((option) => (
                <Button
                  key={option.name}
                  onClick={option.action}
                  variant="outline"
                  className={`border-${option.color}-300 text-${option.color}-600 hover:bg-${option.color}-50`}
                >
                  {option.name}
                </Button>
              ))}
            </div>

            <div className="text-center text-sm text-gray-600">
              <p className="mb-2">🎁 <strong>Referred friends get:</strong> 7-day free trial + 10% off first purchase</p>
              <p>💕 <strong>You get rewards</strong> for each friend who joins the Constant Love community</p>
            </div>
          </div>
        </Card>

        {/* Reward Tiers */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">Reward Tiers</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier, index) => {
              const Icon = tier.icon;
              const isUnlocked = stats.totalReferrals >= tier.referrals;
              const isCurrent = currentTier.id === tier.id && stats.totalReferrals < (nextTier?.referrals || Infinity);
              
              return (
                <Card key={tier.id} className={`p-6 text-center transition-all duration-300 transform hover:scale-105 ${
                  isUnlocked 
                    ? `bg-gradient-to-br from-${tier.color}-50 to-${tier.color}-100 border-${tier.color}-300 shadow-lg` 
                    : 'bg-gray-50 border-gray-200'
                } ${isCurrent ? 'ring-2 ring-rose-400' : ''}`}>
                  
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    isUnlocked ? `bg-${tier.color}-500` : 'bg-gray-300'
                  }`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className={`font-bold text-lg mb-2 ${isUnlocked ? `text-${tier.color}-800` : 'text-gray-500'}`}>
                    {tier.title}
                  </h3>
                  
                  <div className={`text-sm font-medium mb-3 ${isUnlocked ? `text-${tier.color}-600` : 'text-gray-400'}`}>
                    {tier.referrals} referral{tier.referrals > 1 ? 's' : ''}
                  </div>
                  
                  <div className={`font-semibold mb-3 ${isUnlocked ? 'text-gray-800' : 'text-gray-500'}`}>
                    {tier.reward}
                  </div>
                  
                  <p className={`text-xs leading-relaxed ${isUnlocked ? 'text-gray-700' : 'text-gray-400'}`}>
                    {tier.description}
                  </p>
                  
                  {isUnlocked && (
                    <div className="mt-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium bg-${tier.color}-500 text-white`}>
                        Unlocked ✓
                      </span>
                    </div>
                  )}
                  
                  {isCurrent && (
                    <div className="mt-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-rose-500 text-white animate-pulse">
                        Current Tier
                      </span>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <Card className="p-8 text-center bg-gradient-to-r from-rose-100 via-pink-100 to-rose-100 border-rose-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Spread the Love?</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Start sharing your referral code today and help friends discover the power of daily self-love affirmations while earning amazing rewards!
          </p>
          <Button onClick={copyReferralLink} size="lg" className="bg-rose-500 hover:bg-rose-600 text-white rounded-full px-8 py-3 font-medium">
            <Share2 className="w-5 h-5 mr-2" />
            Start Referring Friends
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Card>
        </>
        )}
      </div>
    </div>
  );
};

export default Referrals;